<template>
  <div
    :class="[isVisible && $style.isVisible, $style.detailWrap]"
    :style="hotspotCardStyles"
  >
    <div v-if="borderFrame" aria-hidden="true" :class="$style.border" />
    <div :class="$style.detail">
      <BaseButton strip :class="$style.closeDetail" @click="onRequestClose">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-x-circle"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </BaseButton>

      <MarkedParser :class="$style.detailCopy" :md="hotspot?.fields?.copy" />

      <div v-if="hotspot && hotspot?.fields?.image" :class="$style.detailImg">
        <AspectRatio
          :ratio="`${hotspot?.fields?.image?.fields?.file?.details?.image?.width}x${hotspot?.fields?.image?.fields?.file?.details?.image?.height}`"
        >
          <picture>
            <source
              :srcset="`${hotspot?.fields?.image?.fields?.file?.url}?fm=webp`"
              type="image/webp"
            />
            <img
              :class="$style.backdrop"
              :src="hotspot?.fields?.image?.fields?.file?.url"
              loading="lazy"
              alt=""
            />
          </picture>
        </AspectRatio>
      </div>
    </div>
    <div v-if="borderFrame" aria-hidden="true" :class="$style.border" />
  </div>
</template>

<script setup lang="ts">
import type { CSSProperties } from 'vue'
import type {
  TypeHotspotsMapWithoutUnresolvableLinksResponse,
  TypeHotspotWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const emit = defineEmits(['request-close'])

const props = defineProps<{
  hotspot: TypeHotspotWithoutUnresolvableLinksResponse
  borderFrame?: TypeHotspotsMapWithoutUnresolvableLinksResponse['fields']['spotBorderFrame']
  borderHeight?: TypeHotspotsMapWithoutUnresolvableLinksResponse['fields']['spotBorderHeight']
  isVisible?: boolean
}>()

const hotspotCardStyles = computed(
  () =>
    ({
      '--border-height': props.borderHeight || 0,
      '--border-image': props.borderFrame
        ? `url(${props?.borderFrame?.fields?.file?.url})`
        : null,
    }) as CSSProperties,
)

const onRequestClose = () => {
  emit('request-close')
}

// const generateRandomNum = () => {
//   return random(1, 100) // 1 to 100
// }
</script>

<!-- <script>
import random from 'lodash/random'

export default {
  name: 'HotspotCard',
  components: {
    CloseIcon: () => import('~/assets/icons/x-circle.svg?inline'),
  },
  props: {
    hotspot: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    borderFrame: {
      type: Object,
      required: false,
      default: () => null,
    },
    borderHeight: {
      type: String,
      required: false,
      default: '',
    },
    isVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      // for options
      activeCopyOptionIndex: 0,

      // for grouped options
      activeCopyOptionsGroup: {},
      activeCopyOptionsGroupOptionIndex: 0,
    }
  },
  computed: {
    computedHotspotType() {
      if (this.hotspot?.copyOptionsGroups?.length) {
        return 'hotspotWithCopyOptionsGroups'
      }

      if (this.hotspot?.copyOptions?.length) {
        return 'hotspotWithCopyOptions'
      }

      return 'default'
    },
    computedCopyOptions() {
      return this.hotspot?.copyOptions || []
    },
    computedCopyOptionsGroups() {
      return this.hotspot?.copyOptionsGroups || []
    },
    computedCopyOptionsGroupsWithRange() {
      if (!this.computedCopyOptionsGroups.length) return []
      let currRangeStart = 0
      return this.computedCopyOptionsGroups.map((g) => {
        const range = []
        const odds = Number(g.probability)
        for (let i = 0; i < odds; i++) {
          range.push(currRangeStart + 1)
          currRangeStart = currRangeStart + 1
        }
        return {
          ...g,
          range,
        }
      })
    },
  },
  mounted() {
    if (this.computedHotspotType === 'hotspotWithCopyOptions') {
      this.activeCopyOptionIndex = random(
        0,
        this.hotspot.copyOptions.length - 1,
      )
    }
    if (this.computedHotspotType === 'hotspotWithCopyOptionsGroups') {
      const num = this.generateRandomNum()
      const groupPicked = this.computedCopyOptionsGroupsWithRange.find((g) =>
        g.range.includes(num),
      )

      this.activeCopyOptionsGroup = groupPicked
      this.activeCopyOptionsGroupOptionIndex = random(
        0,
        groupPicked.references.length - 1,
      )
    }
  },
  methods: {
    onRequestClose() {
      this.$emit('request-close')
    },
    generateRandomNum() {
      return random(1, 100) // 1 to 100
    },
  },
}
</script> -->

<style lang="scss" module>
.detailWrap {
  margin: 1em 0;

  opacity: 0;
  transform: translate3d(0, 20px, 0);
  transition: 400ms ease;

  &.isVisible {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.border {
  background-image: var(--border-image);
  background-repeat: repeat-x;
  background-size: contain;
  height: var(--border-height);
  position: relative;
  z-index: 1;
}

.detail {
  box-shadow: 0px 4px 24px -6px var(--black-alpha75);
  background-color: var(--white);
  backface-visibility: hidden;
  color: var(--detail-copy-color);

  width: 100vw;
  max-width: var(--detail-max-width);
  max-height: var(--detail-max-height);
  overflow: hidden;
  position: relative;
}

.detailImg {
  opacity: 0;
  transform: translate3d(0, 20px, 0);
  transition: 400ms var(--easeOutCubic) 250ms;
  width: 100%;

  .isVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.detailCopy {
  @include fluid-type(12px, 16px);
  transform: translate3d(0, 20px, 0);
  transition: 400ms var(--easeOutCubic) 200ms;
  opacity: 0;

  .isVisible & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--purple);
    font-size: 1.25em;
    font-weight: 600;
    text-transform: none;
  }

  p {
    margin: 0.5em 0;
  }

  padding: 1.5em;
}

.closeDetail.closeDetail {
  --button-min-width: none;
  --button-padding: 0;
  --button-border: 0;
  --button-background: none;

  --button-background--hover: none;
  --button-background--active: none;

  border-radius: 50%;
  color: inherit !important;
  display: grid;
  place-content: center;
  padding: 0;
  width: 24px;
  height: 24px;
  position: sticky;
  right: 10px;
  top: 10px;
  float: right;
  z-index: 1;
}

.backdrop {
  display: block;
}
</style>
